export const TOOL_CONTENT_DATA = {
  gradient: {
    heading: 'SwiftUI Gradient Generator',
    intro: [
      'Create SwiftUI gradients fast: LinearGradient, RadialGradient, and AngularGradient with multiple color stops.',
      'Use the generator below to customize colors and export ready-to-paste SwiftUI code.',
    ],
    examples: [
      {
        title: 'LinearGradient background',
        code: `LinearGradient(\n  colors: [.orange, .red],\n  startPoint: .top,\n  endPoint: .bottom\n)\n.ignoresSafeArea()`,
      },
      {
        title: 'Gradient button style',
        code: `Button("Continue") {}\n  .padding(.vertical, 12)\n  .padding(.horizontal, 16)\n  .background(\n    LinearGradient(colors: [.purple, .blue], startPoint: .leading, endPoint: .trailing)\n  )\n  .foregroundStyle(.white)\n  .clipShape(RoundedRectangle(cornerRadius: 12))`,
      },
    ],
  },
  loader: {
    heading: 'SwiftUI Loader Generator',
    intro: [
      'Generate SwiftUI loading indicators for common patterns (ring, pulse, dots) with configurable colors and sizing.',
      'Copy the code output and drop it into your SwiftUI project.',
    ],
    examples: [
      {
        title: 'Overlay loader while loading',
        code: `ZStack {\n  ContentView()\n\n  if isLoading {\n    Color.black.opacity(0.25).ignoresSafeArea()\n    ProgressView().scaleEffect(1.2)\n  }\n}`,
      },
    ],
  },
  card: {
    heading: 'SwiftUI Card Generator',
    intro: [
      'Build reusable SwiftUI card components for lists and grids with shadows, backgrounds, and rounded corners.',
      'Use it to prototype UI fast and standardize your card styles.',
    ],
    examples: [
      {
        title: 'Simple card container',
        code: `VStack(alignment: .leading, spacing: 8) {\n  Text("Title").font(.headline)\n  Text("Subtitle").foregroundStyle(.secondary)\n}\n.padding(16)\n.background(.background)\n.clipShape(RoundedRectangle(cornerRadius: 12))\n.shadow(color: .black.opacity(0.08), radius: 12, x: 0, y: 6)`,
      },
    ],
  },
  animation: {
    heading: 'SwiftUI Animation Generator',
    intro: [
      'Create common SwiftUI animations (fade, slide, bounce, shimmer) with tunable durations and easing curves.',
      'Use the presets to get production-ready animation code quickly.',
    ],
    examples: [
      {
        title: 'Fade + move transition',
        code: `Text("Hello")\n  .transition(.opacity.combined(with: .move(edge: .bottom)))\n  .animation(.easeInOut(duration: 0.35), value: isPresented)`,
      },
    ],
  },
  alert: {
    heading: 'SwiftUI Alert Generator',
    intro: [
      'Generate custom SwiftUI alert/popup components with common variants (success, error, warning, info).',
      'Copy the code and customize visuals to match your app theme.',
    ],
    examples: [
      {
        title: 'Basic SwiftUI alert',
        code: `Button("Delete") { showAlert = true }\n.alert("Are you sure?", isPresented: $showAlert) {\n  Button("Delete", role: .destructive) {}\n  Button("Cancel", role: .cancel) {}\n}`,
      },
    ],
  },
  photoPicker: {
    heading: 'SwiftUI Photo Picker Generator',
    intro: [
      'Generate SwiftUI photo picker code for common camera/gallery flows, including multi-select patterns.',
      'Great for quickly wiring user avatars, attachments, and media selection.',
    ],
    examples: [
      {
        title: 'PhotosPicker (iOS 16+)',
        code: `import PhotosUI\n\n@State private var selection: PhotosPickerItem?\n\nPhotosPicker(selection: $selection, matching: .images) {\n  Label("Select photo", systemImage: "photo")\n}`,
      },
    ],
  },
  jsonToModel: {
    heading: 'JSON to Swift Codable Generator',
    intro: [
      'Convert JSON into Swift Codable models with nested types and CodingKeys.',
      'Paste JSON, tune options, and copy the generated structs.',
    ],
    examples: [
      {
        title: 'Decode JSON into a model',
        code: `let model = try JSONDecoder().decode(Model.self, from: data)`,
      },
    ],
  },
  apiGenerator: {
    heading: 'Swift API Client Generator',
    intro: [
      'Generate a Swift networking layer using URLSession with async/await and structured error handling.',
      'Use it to bootstrap a clean APIService and endpoints quickly.',
    ],
    examples: [
      {
        title: 'Async/await request skeleton',
        code: `let (data, response) = try await URLSession.shared.data(for: request)`,
      },
    ],
  },
  postmanImporter: {
    heading: 'Postman to Swift API Generator',
    intro: [
      'Import a Postman collection and generate Swift API client code from your saved endpoints.',
      'Useful for quickly scaffolding a typed networking layer from an existing API spec.',
    ],
    examples: [],
  },
  storyboardConverter: {
    heading: 'UIKit to SwiftUI Converter',
    intro: [
      'Convert storyboard XML to SwiftUI code to accelerate UIKit → SwiftUI migrations.',
      'Upload or paste storyboard content and iterate on the generated SwiftUI output.',
    ],
    examples: [],
  },
  shadow: {
    heading: 'SwiftUI Shadow Generator',
    intro: [
      'Generate `.shadow(color:radius:x:y)` code for SwiftUI views.',
      'Tune opacity, radius, and offsets until the shadow matches your UI.',
    ],
    examples: [
      {
        title: 'Card shadow',
        code: `.shadow(color: .black.opacity(0.12), radius: 16, x: 0, y: 8)`,
      },
    ],
  },
  button: {
    heading: 'SwiftUI Button Builder',
    intro: [
      'Build styled SwiftUI buttons quickly with padding, backgrounds, corner radius, and borders.',
      'Use it to standardize primary/secondary button styles across your app.',
    ],
    examples: [
      {
        title: 'Primary button',
        code: `Button("Continue") {}\n  .padding(.vertical, 12)\n  .padding(.horizontal, 16)\n  .background(Color.blue)\n  .foregroundStyle(.white)\n  .clipShape(RoundedRectangle(cornerRadius: 12))`,
      },
    ],
  },
  grid: {
    heading: 'SwiftUI Grid Generator',
    intro: [
      'Generate `LazyVGrid` layouts with fixed or adaptive columns and spacing.',
      'Perfect for galleries, product grids, dashboards, and card lists.',
    ],
    examples: [
      {
        title: 'Adaptive columns',
        code: `let columns = [GridItem(.adaptive(minimum: 120), spacing: 12)]\n\nLazyVGrid(columns: columns, spacing: 12) {\n  ForEach(items) { item in\n    Cell(item: item)\n  }\n}`,
      },
    ],
  },
  palette: {
    heading: 'SwiftUI Color Palette Generator',
    intro: [
      'Generate a SwiftUI color palette from hex values with named colors.',
      'Useful for building a lightweight design system and keeping colors consistent.',
    ],
    examples: [
      {
        title: 'Named colors via extension',
        code: `extension Color {\n  static let brandPrimary = Color("brandPrimary")\n  static let brandSecondary = Color("brandSecondary")\n}`,
      },
    ],
  },
};

export const getToolContentData = (seoKey) => {
  return TOOL_CONTENT_DATA[seoKey] || null;
};

